/**
 * Centralized validation rules for Vuetify forms
 * Usage: import { rules } from '@/utils/validation'
 */

export const rules = {
  /** Field is required (non-empty) */
  required: (message = 'Required') =>
    (value: unknown) => !!value || message,

  /** String must have minimum length */
  minLength: (min: number, message?: string) =>
    (value: string) => !value || value.length >= min || message || `Minimum ${min} characters`,

  /** String must have maximum length */
  maxLength: (max: number, message?: string) =>
    (value: string) => !value || value.length <= max || message || `Maximum ${max} characters`,

  /** File size must be under limit (in MB) */
  maxFileSize: (mb: number, message?: string) =>
    (value: File | undefined | null) =>
      !value || value.size < mb * 1_000_000 || message || `File must be less than ${mb}MB`,

  /** Value must be a valid number */
  numeric: (message = 'Must be a number') =>
    (value: string | number) => {
      if (!value && value !== 0) return message
      const num = typeof value === 'string' ? parseFloat(value) : value
      return (!isNaN(num) && isFinite(num)) || message
    },

  /** Value must be a positive number */
  positiveNumber: (message = 'Must be a positive number') =>
    (value: string | number) => {
      if (!value && value !== 0) return message
      const num = typeof value === 'string' ? parseFloat(value) : value
      return (!isNaN(num) && isFinite(num) && num > 0) || message
    },

  /** Email format validation */
  email: (message = 'Invalid email address') =>
    (value: string) => {
      if (!value) return true // Use with required() if needed
      const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return pattern.test(value) || message
    },

  /** Array must have at least one item */
  arrayNotEmpty: (message = 'At least one item required') =>
    (value: unknown[]) => (Array.isArray(value) && value.length > 0) || message,

  /** API object was selected (for autocomplete) */
  apiObjectSelected: (message = 'Please select an item') =>
    (value: unknown) => {
      if (Array.isArray(value)) {
        return value.length > 0 || message
      }
      return !!value || message
    }
}

/** Common rule combinations */
export const commonRules = {
  /** Required text field with minimum 3 characters */
  requiredText: [rules.required(), rules.minLength(3)],

  /** Required name field */
  requiredName: [rules.required('Enter a name')],

  /** Required quantity (numeric) */
  requiredQuantity: [rules.required('Enter a quantity'), rules.numeric('Must be a valid number')],

  /** Image file with 10MB limit */
  imageFile: [rules.maxFileSize(10, 'Image must be less than 10MB')]
}
